/**
 * BrushReveal — 右上→左下に一筆でトゲトゲの筆マスクが走って画像が出る演出
 */
export class BrushReveal {
    constructor(canvas, opts = {}) {
        this.canvas = canvas;
        this.opt = {
            src: "",
            width: 613,
            height: 867,
            delay: 1000,
            duration: 800,
            maxFrameDelta: 32,
            bristles: 55,
            bristleWidth: [20, 50],
            flash: 0,
            gradientOpacity: 0.9,
            gradientEnd: 0.6,
            coverColor: null,
            onStart: null,
            ...opts,
        };
        this.ctx = canvas.getContext("2d");
        this.img = new Image();
        this._raf = 0;
        this._delayTimer = 0;
    }

    load() {
        return new Promise((resolve, reject) => {
            if (this.img.complete && this.img.naturalWidth && this.img.src) {
                resolve();
                return;
            }
            this.img.onload = () => resolve();
            this.img.onerror = reject;
            this.img.decoding = "async";
            this.img.src = this.opt.src;
        });
    }

    _setup() {
        const { width: baseWidth, height: baseHeight } = this.opt;
        const cssWidth = this.canvas.getBoundingClientRect().width || baseWidth;
        const scale = Math.min((cssWidth / baseWidth) * (window.devicePixelRatio || 1), 1);
        const pixelWidth = Math.round(baseWidth * scale);
        const pixelHeight = Math.round(baseHeight * scale);
        const createCanvas = () => Object.assign(document.createElement("canvas"), { width: pixelWidth, height: pixelHeight });

        this.canvas.width = pixelWidth;
        this.canvas.height = pixelHeight;
        this.maskCanvas = createCanvas();
        this.imageCanvas = createCanvas();
        this.maskContext = this.maskCanvas.getContext("2d");
        this.imageContext = this.imageCanvas.getContext("2d");
        [this.ctx, this.maskContext, this.imageContext].forEach((context) => context.setTransform(scale, 0, 0, scale, 0, 0));
        this.imageGradient = null;
        if (this.opt.gradientOpacity > 0) {
            this.imageGradient = this.imageContext.createLinearGradient(0, baseHeight, 0, 0);
            this.imageGradient.addColorStop(0, `rgba(0, 0, 0, ${this.opt.gradientOpacity})`);
            this.imageGradient.addColorStop(this.opt.gradientEnd, "rgba(0, 0, 0, 0)");
        }

        const start = { x: baseWidth + 80, y: -80 };
        const end = { x: -80, y: baseHeight + 80 };
        const length = Math.hypot(end.x - start.x, end.y - start.y);
        const direction = { x: (end.x - start.x) / length, y: (end.y - start.y) / length };
        const normal = { x: -direction.y, y: direction.x };
        const span = (baseWidth * Math.abs(normal.x) + baseHeight * Math.abs(normal.y)) / 2 + 40;
        this.geometry = { width: baseWidth, height: baseHeight, start, length, direction, normal, span };

        const {
            bristles,
            bristleWidth: [lineWidthMin, lineWidthMax],
        } = this.opt;
        const random = (min, max) => min + Math.random() * (max - min);
        this.brushes = Array.from({ length: bristles }, (_, index) => {
            const ratio = -1 + (2 * (index + Math.random())) / bristles;
            return {
                offset: ratio * span,
                lineWidth: random(lineWidthMin, lineWidthMax),
                lag: random(0, 0.08),
                wobble: random(-10, 10),
                tip: random(0.8, 2.2),
            };
        });
    }

    _point(progress, offset) {
        const { start, length, direction, normal } = this.geometry;
        return {
            x: start.x + direction.x * length * progress + normal.x * offset,
            y: start.y + direction.y * length * progress + normal.y * offset,
        };
    }

    _line(brush, progress) {
        if (progress <= 0) return;
        const context = this.maskContext;
        context.lineWidth = brush.lineWidth;
        context.beginPath();
        for (let index = 0; index <= 12; index += 1) {
            const position = (index / 12) * progress;
            const point = this._point(position, brush.offset + Math.sin(position * Math.PI) * brush.wobble);
            if (index) context.lineTo(point.x, point.y);
            else context.moveTo(point.x, point.y);
        }
        context.stroke();
    }

    _spike(brush, progress) {
        if (progress <= 0) return;
        const { normal } = this.geometry;
        const context = this.maskContext;
        const base = Math.max(0, progress - 0.09 * brush.tip);
        this._line(brush, base);
        const start = this._point(base, brush.offset);
        const end = this._point(progress, brush.offset + brush.wobble * 0.5);
        const halfWidth = brush.lineWidth / 2;

        context.beginPath();
        context.moveTo(start.x + normal.x * halfWidth, start.y + normal.y * halfWidth);
        context.lineTo(end.x, end.y);
        context.lineTo(start.x - normal.x * halfWidth, start.y - normal.y * halfWidth);
        context.closePath();
        context.fill();
    }

    _body(progress) {
        if (progress <= 0) return;
        const { span } = this.geometry;
        const context = this.maskContext;
        const pointA = this._point(0, -span - 40);
        const pointB = this._point(0, span + 40);
        const pointC = this._point(progress, span + 40);
        const pointD = this._point(progress, -span - 40);

        context.beginPath();
        context.moveTo(pointA.x, pointA.y);
        context.lineTo(pointB.x, pointB.y);
        context.lineTo(pointC.x, pointC.y);
        context.lineTo(pointD.x, pointD.y);
        context.closePath();
        context.fill();
    }

    _drawCover(context) {
        const { width, height } = this.geometry;
        const imageWidth = this.img.naturalWidth;
        const imageHeight = this.img.naturalHeight;
        const scale = Math.max(width / imageWidth, height / imageHeight);
        const drawWidth = imageWidth * scale;
        const drawHeight = imageHeight * scale;
        context.drawImage(this.img, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    }

    _frame(time) {
        const { width, height } = this.geometry;
        const maskContext = this.maskContext;
        const imageContext = this.imageContext;
        const clamp = (value) => Math.min(Math.max(value, 0), 1);
        const easeOutQuart = (value) => 1 - Math.pow(1 - value, 4);
        const strokeWindow = 0.55 * 0.85;

        maskContext.clearRect(0, 0, width, height);
        maskContext.fillStyle = maskContext.strokeStyle = "#fff";
        maskContext.lineCap = "round";
        maskContext.lineJoin = "round";
        this.brushes.forEach((brush) => this._spike(brush, easeOutQuart(clamp((time - brush.lag) / strokeWindow)) * 1.08));
        this._body(easeOutQuart(clamp((time - 0.16) / strokeWindow)) * 1.05);

        if (this.opt.coverColor) {
            this.ctx.globalCompositeOperation = "source-over";
            this.ctx.clearRect(0, 0, width, height);
            this.ctx.fillStyle = this.opt.coverColor;
            this.ctx.fillRect(0, 0, width, height);
            this.ctx.globalCompositeOperation = "destination-out";
            this.ctx.drawImage(this.maskCanvas, 0, 0, width, height);
            this.ctx.globalCompositeOperation = "source-over";
            return;
        }

        imageContext.globalCompositeOperation = "source-over";
        imageContext.clearRect(0, 0, width, height);
        this._drawCover(imageContext);
        const flashOpacity = this.opt.flash * (1 - clamp(time / 0.6));
        if (flashOpacity > 0) {
            imageContext.globalCompositeOperation = "lighter";
            imageContext.fillStyle = `rgba(255,255,255,${flashOpacity})`;
            imageContext.fillRect(0, 0, width, height);
        }
        if (this.imageGradient) {
            imageContext.globalCompositeOperation = "source-over";
            imageContext.fillStyle = this.imageGradient;
            imageContext.fillRect(0, 0, width, height);
        }
        imageContext.globalCompositeOperation = "destination-in";
        imageContext.drawImage(this.maskCanvas, 0, 0, width, height);

        this.ctx.clearRect(0, 0, width, height);
        this.ctx.drawImage(this.imageCanvas, 0, 0, width, height);
    }

    play() {
        cancelAnimationFrame(this._raf);
        clearTimeout(this._delayTimer);
        this._setup();
        if (this.opt.coverColor) this._frame(0);
        return new Promise((resolve) => {
            this._delayTimer = setTimeout(() => {
                this._delayTimer = 0;
                if (typeof this.opt.onStart === "function") this.opt.onStart();
                const duration = this.opt.duration;
                let elapsed = 0;
                let previousAt = null;
                const loop = (now) => {
                    if (previousAt === null) previousAt = now;
                    elapsed += Math.min(now - previousAt, this.opt.maxFrameDelta);
                    previousAt = now;
                    const time = Math.min(elapsed / duration, 1);
                    this._frame(time);
                    if (time < 1) this._raf = requestAnimationFrame(loop);
                    else resolve();
                };
                this._raf = requestAnimationFrame(loop);
            }, this.opt.delay);
        });
    }

    stop() {
        cancelAnimationFrame(this._raf);
        clearTimeout(this._delayTimer);
        this._delayTimer = 0;
    }
}
